import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

// BRN-2118 (AC9): window-management state for the floating chat panel -- open/closed,
// attached/detached, and geometry (position + size), all clamped to the viewport and persisted
// per-session, host-side, in sessionStorage. Deliberately carries NO token/auth material (the
// decision record: "NEVER token material") -- this service only ever stores UI chrome geometry.
// See lifecycle/decisions/floating-chat-entry-point.md for the full binding UX contract this
// implements (docked/detached min/max bounds, 48px-header-visible clamping rule, <640px fullscreen
// breakpoint with detach/resize disabled).

export interface Size {
  width: number;
  height: number;
}

export interface Position {
  x: number;
  y: number;
}

export type PanelGeometry = Size & Position;

export interface AgentWindowState {
  isOpen: boolean;
  isDetached: boolean;
  /**
   * Full-viewport overlay. Independent of `isDetached`: geometry is ignored while true and restored
   * untouched on exit, so leaving fullscreen returns the panel to exactly the docked/detached size
   * and position it had before. Never persisted as `true` — a reload should not trap the host app
   * under a full-screen chat the user does not remember opening.
   */
  isFullscreen: boolean;
  geometry: PanelGeometry;
}

export const DOCKED_MIN: Size = { width: 320, height: 420 };
export const DETACHED_MIN: Size = DOCKED_MIN;
export const SMALL_SCREEN_BREAKPOINT_PX = 640;
export const HEADER_VISIBLE_MIN_PX = 48;
const STORAGE_KEY = 'brainAgentChatWindowState';

export function dockedMax(viewportWidth: number, viewportHeight: number): Size {
  return {
    width: Math.min(480, viewportWidth * 0.92),
    height: Math.min(viewportHeight * 0.85, 800),
  };
}

export function detachedMax(viewportWidth: number, viewportHeight: number): Size {
  return {
    width: viewportWidth * 0.92,
    height: viewportHeight * 0.92,
  };
}

export function dockedDefaultSize(viewportHeight: number): Size {
  return { width: 400, height: Math.min(600, viewportHeight * 0.8) };
}

export function isSmallScreen(viewportWidth: number): boolean {
  return viewportWidth < SMALL_SCREEN_BREAKPOINT_PX;
}

/**
 * Clamps a candidate size to [min, max] independently per axis -- min always wins over max if
 * they ever conflict (a pathologically tiny viewport), so the panel never renders at a negative
 * or inverted size.
 */
export function clampSize(candidate: Size, min: Size, max: Size): Size {
  return {
    width: Math.max(min.width, Math.min(candidate.width, Math.max(min.width, max.width))),
    height: Math.max(min.height, Math.min(candidate.height, Math.max(min.height, max.height))),
  };
}

/**
 * Clamps a candidate top-left position so at least HEADER_VISIBLE_MIN_PX of the panel's header
 * strip (its top edge, full width) stays inside the viewport on every side -- the decision
 * record's "geometry always clamped so >=48px of the header stays in-viewport" rule. Never lets
 * the header go above the viewport top or below (viewportHeight - HEADER_VISIBLE_MIN_PX); never
 * lets it go further left/right than leaving HEADER_VISIBLE_MIN_PX of width on-screen.
 */
export function clampPosition(
  candidate: Position,
  size: Size,
  viewportWidth: number,
  viewportHeight: number,
): Position {
  const minX = HEADER_VISIBLE_MIN_PX - size.width;
  const maxX = viewportWidth - HEADER_VISIBLE_MIN_PX;
  const minY = 0;
  const maxY = Math.max(minY, viewportHeight - HEADER_VISIBLE_MIN_PX);
  return {
    x: Math.min(Math.max(candidate.x, minX), Math.max(minX, maxX)),
    y: Math.min(Math.max(candidate.y, minY), maxY),
  };
}

export function clampGeometry(
  candidate: PanelGeometry,
  mode: 'docked' | 'detached',
  viewportWidth: number,
  viewportHeight: number,
): PanelGeometry {
  const min = mode === 'docked' ? DOCKED_MIN : DETACHED_MIN;
  const max = mode === 'docked' ? dockedMax(viewportWidth, viewportHeight) : detachedMax(viewportWidth, viewportHeight);
  const size = clampSize(candidate, min, max);
  const position = clampPosition(candidate, size, viewportWidth, viewportHeight);
  return { ...size, ...position };
}

function defaultDockedGeometry(viewportWidth: number, viewportHeight: number): PanelGeometry {
  const size = dockedDefaultSize(viewportHeight);
  const inset = 24;
  return clampGeometry(
    { ...size, x: viewportWidth - size.width - inset, y: viewportHeight - size.height - inset - 72 },
    'docked',
    viewportWidth,
    viewportHeight,
  );
}

function readPersisted(): Partial<AgentWindowState> | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed as Partial<AgentWindowState>;
  } catch {
    return null;
  }
}

function writePersisted(state: AgentWindowState): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    // Explicit allowlist of persisted fields -- never a token, never anything beyond UI chrome.
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      isOpen: state.isOpen,
      isDetached: state.isDetached,
      geometry: state.geometry,
    }));
  } catch {
    // silent no-op -- a full/blocked storage must never break the panel.
  }
}

@Injectable({ providedIn: 'root' })
export class AgentWindowStateService {
  private readonly viewport = (): { width: number; height: number } => ({
    width: typeof window === 'undefined' ? 1280 : window.innerWidth,
    height: typeof window === 'undefined' ? 800 : window.innerHeight,
  });

  private readonly stateSubject = new BehaviorSubject<AgentWindowState>(this.initialState());
  readonly state$: Observable<AgentWindowState> = this.stateSubject.asObservable();

  get value(): AgentWindowState {
    return this.stateSubject.value;
  }

  get smallScreen(): boolean {
    return isSmallScreen(this.viewport().width);
  }

  open(): void {
    // No mode reset here: close() already returned the panel to its docked anchor, so an open()
    // that follows a close starts docked. A panel left open across in-product navigation keeps
    // whatever arrangement the user chose, which is the point of persisting it.
    this.patch({ isOpen: true });
  }

  close(): void {
    // Closing ends the session's window arrangement: fullscreen, detach and the custom geometry are
    // all dropped so the next launcher click gives back a docked panel anchored to the button.
    // Detach/position only survive while the panel stays OPEN across navigation.
    const { width: vw, height: vh } = this.viewport();
    this.patch({
      isOpen: false,
      isDetached: false,
      isFullscreen: false,
      geometry: defaultDockedGeometry(vw, vh),
    });
  }

  detach(): void {
    if (this.smallScreen) {
      return;
    }
    const { width: vw, height: vh } = this.viewport();
    this.patch({ isDetached: true, geometry: clampGeometry(this.value.geometry, 'detached', vw, vh) });
  }

  reattach(): void {
    const { width: vw, height: vh } = this.viewport();
    this.patch({ isDetached: false, geometry: defaultDockedGeometry(vw, vh) });
  }

  /**
   * Toggles the full-viewport overlay. `geometry` and `isDetached` are left untouched so exiting
   * restores the exact pre-fullscreen window; the CSS ignores both while `--fullscreen` is applied.
   */
  toggleFullscreen(): void {
    this.patch({ isFullscreen: !this.value.isFullscreen });
  }

  /**
   * Applies a drag/resize delta (or absolute candidate) and clamps to the current mode's bounds.
   * BRN-2118 L6 remediation (Cluster F1): defaults to persisting immediately (every pre-existing
   * caller keeps today's behavior), but the live drag/resize pointermove path below passes
   * `persist:false` to keep geometry updates in-memory only during the gesture -- sessionStorage is
   * a synchronous, blocking write, and firing it at pointermove frequency (120-240/sec) was the
   * clearest jank contributor on this feature's signature interaction. Callers that opt out MUST
   * call `persistGeometry()` once the gesture ends.
   */
  updateGeometry(candidate: PanelGeometry, options: { persist?: boolean } = {}): void {
    if (this.smallScreen) {
      return;
    }
    const { width: vw, height: vh } = this.viewport();
    const mode = this.value.isDetached ? 'detached' : 'docked';
    this.patch({ geometry: clampGeometry(candidate, mode, vw, vh) }, options.persist ?? true);
  }

  /** Flushes the current in-memory geometry to sessionStorage -- the gesture-end (pointerup/
   * pointercancel) counterpart to an `updateGeometry(..., { persist: false })` stream of live
   * drag/resize updates (Cluster F1). A no-op write if nothing changed since the last persist. */
  persistGeometry(): void {
    writePersisted(this.value);
  }

  /** Re-clamps the current geometry against the current viewport -- call on window resize. */
  reclampToViewport(): void {
    const { width: vw, height: vh } = this.viewport();
    const mode = this.value.isDetached ? 'detached' : 'docked';
    this.patch({ geometry: clampGeometry(this.value.geometry, mode, vw, vh) });
  }

  private patch(partial: Partial<AgentWindowState>, persist = true): void {
    const next: AgentWindowState = { ...this.value, ...partial };
    this.stateSubject.next(next);
    if (persist) {
      writePersisted(next);
    }
  }

  private initialState(): AgentWindowState {
    const { width: vw, height: vh } = this.viewport();
    const persisted = readPersisted();
    const geometry = persisted?.geometry
      ? clampGeometry(persisted.geometry, persisted.isDetached ? 'detached' : 'docked', vw, vh)
      : defaultDockedGeometry(vw, vh);
    return {
      isOpen: false, // BRN-2118: never resume "open" across a fresh session boot.
      // Always docked on a fresh boot. BRAIN is a SPA, so in-product navigation never re-runs this
      // -- a detached, repositioned panel keeps its arrangement across route changes because the
      // service instance survives. Only a full page reload lands here, and there the panel is
      // closed anyway (isOpen above), so restoring a detached mode would just mean the NEXT launcher
      // click opened a window mid-screen instead of anchored to the button.
      isDetached: false,
      // Deliberately absent from writePersisted's allowlist, so this is always a fresh false.
      isFullscreen: false,
      geometry,
    };
  }
}
